import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';

import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';

import { Subject, takeUntil, distinctUntilChanged, BehaviorSubject, debounceTime, map } from 'rxjs';

import { LoaderOverlayComponent } from '@components/loader-overlay/loader-overlay.component';
import { FavoritesCardsComponent } from '../../components/favorites-cards/favorites-cards.component';

import { UserBehaviorService } from '@services/user-events/user-behavior.service';

import { MenuStateService } from '../../core/services/menu-state.service';
import { MenuService } from '../../core/services/menu.service';

import { ResponseData } from '@shared/helpers/response-data';
import { filterMenuItems, searchMenuItemsFlat } from '../../shared/utils/menu-filter';
import { MenuItem } from '../../shared/models/side-menu.model';
import { HomepageCardMenuComponent } from '../../shared/components/card-menu/card-menu.component';
import { BreadcrumbItem, FilterType, TabButtonGroup } from '../../shared/models';
import { TabGroupComponent } from '../../shared/components/tab-group/tab-group.component';
import { BreadcrumbHomeComponent } from '../../shared/components/breadcrumb/breadcrumb.component';

import { LocalStorageService } from 'src/app/connectors/localstorage';
import { AlertService } from '@services/adaptors/alert.service';
import { ModalActionsService } from '@core/modals/services/modal-actions.service';
import { ModalFeedbackComponent } from '@modals/modal-feedback/modal-feedback.component';
import { FeedbackFabComponent } from '@shared/components/feedback-fab/feedback-fab.component';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    FavoritesCardsComponent,
    FormsModule,
    HomepageCardMenuComponent,
    LoaderOverlayComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    TabGroupComponent,
    BreadcrumbHomeComponent,
    FeedbackFabComponent
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  #alertService = inject(AlertService);
  #menuStateService = inject(MenuStateService);
  #menuService = inject(MenuService);
  #userBehavior = inject(UserBehaviorService);
  #localStorageService = inject(LocalStorageService);
  #modalActionsService = inject(ModalActionsService);

  menuData = signal<ResponseData<MenuItem[]>>({
    data: null,
    status: 'loading'
  });

  #navigationStack = signal<MenuItem[]>([]);
  displayedMenuItems = signal<MenuItem[]>([]);

  breadcrumbItems = computed<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [];
    const navigationStack = this.#navigationStack();

    if (this.selectedCategory() === 'all') {
      const isHomeActive = navigationStack.length === 0;
      items.push({
        label: 'Home',
        isHome: true,
        active: isHomeActive,
        index: 0
      });
    }

    navigationStack.forEach((item, index) => {
      const currentItemIndex = index + 1;

      items.push({
        label: item.title,
        isHome: false,
        active: index === navigationStack.length - 1,
        index: currentItemIndex
      });
    });

    return items;
  });

  tabButtonGroup = signal<TabButtonGroup[]>([
    { label: 'Favorites', value: 'favorites', selected: false },
    { label: 'All Categories', value: 'all', selected: true }
  ]);
  selectedCategory = signal<FilterType>('all');

  selectedUser = signal<{ name: string; email: string } | null>(null);
  readonly #userId = Number(this.#localStorageService.decryptAndGet('userId'));

  #searchTermSubject = new BehaviorSubject<string>('');
  modelSearchTerm: string = '';

  destroy$ = new Subject<boolean>();

  isHome = computed(() => this.#navigationStack().length === 0);

  ngOnInit(): void {
    this.#listenerMenuItems();
    this.#listenerUserBehavior();
    this.#listenerSearchTerm();
  }

  #listenerMenuItems(): void {
    this.menuData.set({
      data: null,
      status: 'loading'
    });
    this.#menuStateService.menuItems$.pipe().subscribe((res) => {
      this.menuData().status = res.status;
      if (res.data) {
        const isEqual = JSON.stringify(this.menuData().data) === JSON.stringify(res.data);
        if (!isEqual) {
          this.menuData().data = res.data;
          this.displayedMenuItems.set(res.data);
        }
      }
    });
  }

  #listenerUserBehavior(): void {
    this.#userBehavior.selectedUser.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((user) => {
      if (user.name && user.email) {
        this.selectedUser.set({ name: user.name, email: user.email });
      }
    });
  }

  #listenerSearchTerm(): void {
    this.#searchTermSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        map((term) => term.toLowerCase().trim()),
        takeUntil(this.destroy$)
      )
      .subscribe((formattedTerm) => {
        if (this.selectedCategory() === 'favorites') {
          this.#navigationStack.set([]);
          const filteredMenuItems = filterMenuItems(this.menuData().data || [], formattedTerm, 'favorites');
          this.displayedMenuItems.set(filteredMenuItems);
          return;
        }

        if (formattedTerm) {
          if (this.selectedCategory() === 'all') {
            if (!this.isHome()) {
              const lastItem = this.#navigationStack()[this.#navigationStack().length - 1];
              const itemsToSearch = lastItem.items ? lastItem.items : [];
              this.displayedMenuItems.set(filterMenuItems(itemsToSearch, formattedTerm));
            } else {
              this.displayedMenuItems.set(filterMenuItems(this.menuData().data || [], formattedTerm));
            }
          }
        } else {
          if (!this.isHome()) {
            const lastItem = this.#navigationStack()[this.#navigationStack().length - 1];
            this.displayedMenuItems.set(lastItem.items ? lastItem.items : []);
          } else {
            this.displayedMenuItems.set(this.menuData().data || []);
          }
        }
      });
  }

  onSearchTermChange(newValue: string): void {
    this.modelSearchTerm = newValue;
    this.#searchTermSubject.next(newValue);
  }

  clearSearchTerm(): void {
    this.modelSearchTerm = '';
    this.#searchTermSubject.next('');
  }

  filterMenuItemsByCategory(categorie: FilterType): void {
    this.selectedCategory.set(categorie);
    this.modelSearchTerm = '';
    this.#searchTermSubject.next('');

    this.#navigationStack.set([]);
    this.displayedMenuItems.set(filterMenuItems(this.menuData().data || [], '', categorie));
  }

  onParentClicked(menuItem: MenuItem): void {
    if (this.modelSearchTerm.length) {
      this.modelSearchTerm = '';
      this.#searchTermSubject.next('');
    }
    if (menuItem.items && menuItem.items.length > 0) {
      this.#navigationStack.update((stack) => [...stack, menuItem]);
      this.displayedMenuItems.set(menuItem.items);
    } else {
      window.open(menuItem.link, '_blank');
    }
  }

  onBreadcrumbClick(clickedIndex: number): void {
    if (this.modelSearchTerm.length) {
      this.modelSearchTerm = '';
      this.#searchTermSubject.next('');
    }
    this.#navigationStack.update((currentStack) => currentStack.slice(0, clickedIndex));
    this.#updateDisplayedMenuItems();
  }

  onToggleFavorite(item: MenuItem): void {
    const newLikedStatus = !item.liked;

    this.#menuService
      .updateFavoriteStatus({ userId: this.#userId, pagePath: item.link! })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          const updatedMenuData = this.#updateFavoriteStatus(this.menuData().data || [], item, newLikedStatus);
          this.menuData.set({
            ...this.menuData(),
            data: updatedMenuData
          });

          this.#menuStateService.setMenuItems(this.menuData());

          this.#navigationStack.update((stack) => {
            return stack.map((navItem) => this.#updateFavoriteStatus([navItem], item, newLikedStatus)[0]);
          });
          this.#updateDisplayedMenuItems();
        },
        error: () => {
          this.#alertService.showDefaultErrorMessage();
        }
      });
  }

  #updateFavoriteStatus(items: MenuItem[], targetItem: MenuItem, liked: boolean): MenuItem[] {
    return items.map((item) => {
      if (item.link && targetItem.link && item.link === targetItem.link) {
        return { ...item, liked: liked };
      }
      if (item.items && item.items.length > 0) {
        const updatedSubItems = this.#updateFavoriteStatus(item.items, targetItem, liked);
        if (updatedSubItems !== item.items) {
          return { ...item, items: updatedSubItems };
        }
      }
      return item;
    });
  }

  #updateDisplayedMenuItems(): void {
    const menuData = this.menuData().data || [];
    const navigationStack = this.#navigationStack();

    if (this.selectedCategory() === 'favorites') {
      this.displayedMenuItems.set(filterMenuItems(menuData, '', 'favorites'));
      this.modelSearchTerm = '';
      this.#searchTermSubject.next('');
      return;
    }

    if (this.modelSearchTerm) {
      if (navigationStack.length === 0) {
        this.displayedMenuItems.set(filterMenuItems(menuData, this.modelSearchTerm, this.selectedCategory()));
      } else {
        const lastItem = navigationStack[navigationStack.length - 1];
        this.displayedMenuItems.set(
          filterMenuItems(lastItem.items ? lastItem.items : [], this.modelSearchTerm, this.selectedCategory())
        );
      }
    } else {
      if (navigationStack.length === 0) {
        this.displayedMenuItems.set(menuData);
      } else {
        const lastItem = navigationStack[navigationStack.length - 1];
        this.displayedMenuItems.set(lastItem.items ? lastItem.items : []);
      }
    }
  }

  openFeedbackModal(): void {
    const userId = Number.isFinite(this.#userId) ? this.#userId : undefined;
    this.#modalActionsService
      .openModal({
        component: ModalFeedbackComponent,
        params: { userId },
        dialogConfig: {
          width: '500px',
          height: '550px',
          panelClass: ['feedback-modal']
        }
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.#searchTermSubject.complete();
  }
}
